import { compressImageFile } from "@bto/shared";
import React, { useEffect, useState } from "react";
import Dropzone, {
  IFileWithMeta,
  IPreviewProps,
  StatusValue,
} from "react-dropzone-uploader";

import {
  FileUploaderTitle,
  FileUploaderDropzone,
  FileUploaderBrowse,
  FileUploaderDropzoneWrapper,
  FileUploaderWrapper,
  FileUploaderPreview,
  FileUploaderPreviewButton,
  FileUploaderPreviewTitle,
  FileUploaderPreviewDelete,
  FileUploaderPreviewError,
  FileUploaderDropzoneLimit,
} from "./FileUploader.styles";
import "react-dropzone-uploader/dist/styles.css";
import { themePalette } from "../../theme";
import { SuccessUploadIcon } from "../../assets/iconComponents/SuccessUploadIcon";
import { ExclamationIcon } from "../../assets/iconComponents/ExclamationIcon";
import {
  toBase64,
  uploadImageForProcessing,
} from "./Services/UserReportService";
import { DetailedRequisitionSerializer } from "../../recoil/types";
import { FileNameUploaded } from "../NewRequisition/NewReqFileUploader.styles";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";

export interface FileUploadInfo {
  [fileName: string]: {
    fileName: string;
    id: string;
    valid: boolean;
  };
}

const FeedbackFileUpload = () => (
  <FileUploaderDropzone key="custom">
    <FileUploaderTitle variant="h6">
      Drag and drop files for instant upload
    </FileUploaderTitle>
    <FileUploaderDropzoneLimit style={{}}>
      {" "}
      Files can be up to 25MB.
    </FileUploaderDropzoneLimit>
    <FileUploaderBrowse>FILE UPLOAD</FileUploaderBrowse>
  </FileUploaderDropzone>
);

interface FileUploaderProps {
  fullReport: DetailedRequisitionSerializer;
  value: FileUploadInfo;
  setValue: React.Dispatch<React.SetStateAction<FileUploadInfo>>;
  uploadProcessing: boolean;
  setUploadProcessing: React.Dispatch<React.SetStateAction<boolean>>;
  setFullReport: React.Dispatch<
    React.SetStateAction<DetailedRequisitionSerializer | undefined>
  >;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  fullReport,
  value,
  setValue,
  uploadProcessing,
  setUploadProcessing,
  setFullReport,
}) => {
  const [dropzoneKey, setDropzoneKey] = useState(0);
  const [activeFiles, setActiveFiles] = useState<FileUploadInfo>({});
  const numberOfFiles = (value && Object.keys(value).length) ?? 0;
  const { auth, setLoggedOut } = useAuthenticationHook();

  const PreviewComponent = ({ meta, fileWithMeta }: IPreviewProps) => {
    const { name, previewUrl } = meta;
    const { remove } = fileWithMeta;

    return (
      <FileUploaderPreview
        previewUrl={previewUrl}
        validUpload={activeFiles[name]?.valid}
      >
        <FileUploaderPreviewTitle>
          {activeFiles[name]?.valid !== undefined &&
            (activeFiles[name]?.valid ? (
              <SuccessUploadIcon />
            ) : (
              <ExclamationIcon color={themePalette.redMedium} />
            ))}
          <FileNameUploaded> {name} </FileNameUploaded>
        </FileUploaderPreviewTitle>
        <FileUploaderPreviewDelete>
          <FileUploaderPreviewError>
            {activeFiles[name]?.valid === false ? "Upload Failed!" : ""}
          </FileUploaderPreviewError>
          {activeFiles[name]?.valid !== undefined && (
            <FileUploaderPreviewButton onClick={() => remove()}>
              Remove
            </FileUploaderPreviewButton>
          )}
        </FileUploaderPreviewDelete>
      </FileUploaderPreview>
    );
  };

  useEffect(() => {
    if (numberOfFiles === 0) {
      setDropzoneKey((key) => key + 1);
      setActiveFiles({});
    }
  }, [numberOfFiles]);

  useEffect(() => {
    setValue(activeFiles);
  }, [activeFiles]);

  const handleChangeStatus: (
    recentFile: IFileWithMeta,
    status: StatusValue,
  ) => void = async (recentFile: IFileWithMeta, status: StatusValue) => {
    const activeFileName = activeFiles[recentFile.file.name]?.fileName;
    const activeID = activeFiles[recentFile.file.name]?.id;

    if (status === "done" && auth) {
      if (!activeFileName) {
        setUploadProcessing(true);
        try {
          const compressedFile = await compressImageFile(recentFile.file);
          const base64Image = await toBase64(compressedFile);
          const response = await uploadImageForProcessing(
            base64Image,
            fullReport.sfid || "",
            auth.token,
            setLoggedOut,
          );
          if (response) {
            setActiveFiles((active) => ({
              ...active,
              [recentFile.file.name]: {
                fileName: recentFile.file.name,
                valid: response.valid,
                id: activeID || recentFile.meta.id,
              },
            }));
            recentFile.restart();
            setFullReport(response.report!);
            if (activeID) {
              recentFile.remove();
            }
          }
        } catch (err) {
          console.error("Treatment notes upload error", err);
        } finally {
          setUploadProcessing(false);
        }
      } else {
        recentFile.remove();
      }
    } else if (status === "removed") {
      const oldActive = { ...activeFiles };
      if (oldActive[recentFile.file.name]?.id === recentFile?.meta.id) {
        delete oldActive[recentFile.file.name];
        setActiveFiles(oldActive);
      }
    }
  };

  return (
    <>
      <FileUploaderWrapper>
        <FileUploaderDropzoneWrapper>
          <Dropzone
            disabled={uploadProcessing}
            key={dropzoneKey}
            accept=".png, .jpg, .jpeg, .pdf"
            onChangeStatus={(recentFile: IFileWithMeta, status: StatusValue) =>
              handleChangeStatus(recentFile, status)
            }
            submitButtonDisabled
            styles={{
              dropzone: {
                border: "none",
                height: "180px",
                overflow: "hidden",
                background: themePalette.neutral50,
                display: "flex",
                flexFlow: "wrap",
                flexDirection: "row",
                overflowY: "scroll",
                alignItems: "start",
                justifyContent: "center",
              },
              submitButtonContainer: {
                display: "none",
              },
              preview: {
                backgroundColor: themePalette.redMedium,
                padding: "20px 3%",
              },
            }}
            inputContent={FeedbackFileUpload()}
            PreviewComponent={PreviewComponent}
          />
        </FileUploaderDropzoneWrapper>
      </FileUploaderWrapper>
    </>
  );
};

export default FileUploader;
